import {
	APP_INITIALIZER,
	CUSTOM_ELEMENTS_SCHEMA,
	Injector,
	LOCALE_ID,
	NgModule,
	NgZone,
	Optional,
	SkipSelf,
} from "@angular/core";
import {CommonModule, LocationStrategy} from "@angular/common";
import {HTTP_INTERCEPTORS, HttpClientModule} from "@angular/common/http";

import {CachingInterceptor} from "./interceptors/caching.interceptor";
import {ServerErrorInterceptor} from "./interceptors/server-error.interceptor";
import {SvgClipPathInterceptor} from "./interceptors/svg-clip-path.interceptor";
import {KeycloakAngularModule, KeycloakBearerInterceptor, KeycloakService} from "keycloak-angular";

import {AppAuthGuard} from "./guards/app.authguard";

import {RouterModule} from "@angular/router";
import {TranslateLoader, TranslateModule, TranslateService} from "@ngx-translate/core";
import {NavigationComponent} from "./components/navigation/navigation.component";
import {MessageComponent} from "./components/message/message.component";
import {BreadcrumbComponent} from "./components/breadcrumb/breadcrumb.component";
import {TRANSLATE_HTTP_LOADER_CONFIG, TranslateHttpLoader} from "@ngx-translate/http-loader";
import {BreadcrumbsModule} from "@exalif/ngx-breadcrumbs";
import {OverlayModule} from "@angular/cdk/overlay";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";

import {BreadcrumbEmpSearchComponent} from "./components/breadcrumb/breadcrumb-emp-search/breadcrumb-emp-search.component";
import {BreadcrumbProjectSearchComponent} from "./components/breadcrumb/breadcrumb-project-search/breadcrumb-project-search.component";
import {BreadcrumbProfileSearchComponent} from "./components/breadcrumb/breadcrumb-profile-search/breadcrumb-profile-search.component";

import {GlobalSearchBoxComponent} from "./components/navigation/global-search-box/global-search-box.component";
import {Title} from "@angular/platform-browser";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatSidenavModule} from "@angular/material/sidenav";
import {MatListModule} from "@angular/material/list";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatButtonModule} from "@angular/material/button";
import {MatInputModule} from "@angular/material/input";

import {initializer, langFactory} from "./initializer/app-init";
import {CustomizingService} from "./services/services/customizing.service";
import {PendingChangesGuard} from "./guards/pending-changes.guard";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {HomescreenModule} from "../feature/homescreen/homescreen.module";
import {BreadcrumbResourceRequestSearchComponent} from "./components/breadcrumb/breadcumb-resource-request-search/breadcrumb-resource-request-search.component";

import {MatCardModule} from "@angular/material/card";
import {MatTabsModule} from "@angular/material/tabs";
import {MatButtonToggleModule} from "@angular/material/button-toggle";
import {MatSelectModule} from "@angular/material/select";
import {MatDialogModule} from "@angular/material/dialog";
import {SidenavModule} from "../shared/sidenav/sidenav.module";
import {MatBadgeModule} from "@angular/material/badge";

import {MatMenuModule} from "@angular/material/menu";
import {MepPopoverModule} from "../shared/mep-popover/mep-popover.module";
import {SwUpdate} from "@angular/service-worker";

// required for AOT compilation
// eslint-disable-next-line @typescript-eslint/naming-convention
export function HttpLoaderFactory() {
	return new TranslateHttpLoader();
}

@NgModule({
	imports: [
		HttpClientModule,
		CommonModule,
		BreadcrumbsModule.forRoot(),
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
			},
		}),
		FormsModule,
		ReactiveFormsModule,
		OverlayModule,
		KeycloakAngularModule,
		MatFormFieldModule,
		MatIconModule,
		MatToolbarModule,
		RouterModule,
		MatSidenavModule,
		MatListModule,
		MatTooltipModule,
		MatButtonModule,
		MatInputModule,
		MatProgressSpinnerModule,
		HomescreenModule,
		MatCardModule,
		MatTabsModule,
		MatButtonToggleModule,
		MatSelectModule,
		MatDialogModule,
		SidenavModule,
		MatBadgeModule,
		MatMenuModule,
		MepPopoverModule,
		GlobalSearchBoxComponent,
		NavigationComponent,
		MessageComponent,
		BreadcrumbComponent,
		BreadcrumbEmpSearchComponent,
		BreadcrumbProjectSearchComponent,
		BreadcrumbProfileSearchComponent,
		BreadcrumbResourceRequestSearchComponent,
	],
	providers: [
		{provide: TRANSLATE_HTTP_LOADER_CONFIG, useValue: {prefix: "./assets/i18n/", suffix: ".json"}},
		{provide: HTTP_INTERCEPTORS, useClass: SvgClipPathInterceptor, multi: true},
		{provide: HTTP_INTERCEPTORS, useClass: CachingInterceptor, multi: true},
		{provide: HTTP_INTERCEPTORS, useClass: ServerErrorInterceptor, multi: true},
		{provide: HTTP_INTERCEPTORS, useClass: KeycloakBearerInterceptor, multi: true},
		AppAuthGuard,
		TranslateService,
		{provide: LOCALE_ID, useFactory: langFactory, deps: [TranslateService]},
		{
			provide: APP_INITIALIZER,
			useFactory: initializer,
			multi: true,
			deps: [KeycloakService, CustomizingService, TranslateService, Injector, LocationStrategy, NgZone, SwUpdate],
		},
		Title,
		PendingChangesGuard,
	],
	exports: [
		TranslateModule,
		FormsModule,
		ReactiveFormsModule,
		OverlayModule,
		KeycloakAngularModule,
		NavigationComponent,
		BreadcrumbComponent,
		BreadcrumbEmpSearchComponent,
		BreadcrumbProjectSearchComponent,
		BreadcrumbProfileSearchComponent,
		MessageComponent,
		GlobalSearchBoxComponent,
	],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CoreModule {
	constructor(@Optional() @SkipSelf() otherCoreModule: CoreModule) {
		if (otherCoreModule) {
			throw new Error("Core module is only allowed to be loaded once!.");
		}
	}
}
